import React, { Fragment, useEffect } from 'react';
import { preventOverflow } from '../../web3/utils';
import clsx from 'clsx';
import ReactSlider from 'react-slider';
import useDebounce from '../../hooks/useDebounce';
import { config } from '../../config/config';
import LeverageInput from './LeverageInput';
import { parseEther } from 'ethers/lib/utils';

type Props = {
    value: string;
    minLeverage?: string;
    disabled?: boolean;
    min?: number;
    max?: number;
    marks: Array<{ label: string; index: number }>;
    decimalPlace?: number;
    onChange: (newValue: string) => void;
};

const LeverageSlider: React.FC<Props> = ({
    value,
    minLeverage,
    disabled = false,
    min = 1,
    max = 100,
    marks,
    decimalPlace = 1,
    onChange,
}) => {
    const validateLeverage = (value: string) => {
        if (
            !value ||
            parseEther(value).lt(parseEther(minLeverage || config.minLeverage.toString()))
        ) {
            onChange(minLeverage || config.minLeverage.toString());
        }
    };

    const handleDebouncedChange = useDebounce(validateLeverage, 3000);

    useEffect(() => {
        handleDebouncedChange(value);
    }, [value, handleDebouncedChange]);

    const handleChange = (value: string) => {
        onChange(value);
    };

    const handleSliderChange = (value: number) => {
        if (minLeverage) {
            if (value > parseFloat(minLeverage)) {
                handleChange(value.toString());
            } else {
                handleChange(minLeverage);
            }

            return;
        }
        handleChange(value.toString());
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        handleChange(preventOverflow(value, decimalPlace));
    };

    return (
        <div className='leverage-slider'>
            <div className='slider-header'>
                <span>Leverage slider</span>
                <LeverageInput
                    leverage={value}
                    maxLeverage={max}
                    onChange={handleTextChange}
                    disabled={disabled}
                />
            </div>
            <ReactSlider
                className='horizontal-slider'
                markClassName='slider-marker'
                thumbClassName='slider-thumb'
                trackClassName='slider-track'
                min={min}
                max={max}
                marks={marks.map((mark) => mark.index)}
                disabled={disabled}
                renderMark={(props) => {
                    const curMark = marks.find(({ index }) => index === props.key);

                    return curMark ? (
                        <span
                            {...props}
                            className={clsx(props.className, { disabled })}
                            style={{
                                ...props.style,
                                left: props.key === 0 ? 0 : props.style?.left,
                            }}
                            onClick={
                                disabled ? undefined : () => handleChange(curMark.label)
                            }
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !disabled) {
                                    handleChange(curMark.label);
                                }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            tabIndex={disabled ? -1 : 0}
                        >
                            {curMark.label}x
                        </span>
                    ) : null;
                }}
                renderTrack={(props) => (
                    <Fragment key={props.key}>
                        {minLeverage && minLeverage !== config.minLeverage.toString() ? (
                            <div
                                className={clsx('disabled-track', props.className)}
                                style={{
                                    ...props.style,
                                    right: 'unset',
                                    width: `${
                                        (Number(minLeverage) /
                                            (100 - config.minLeverage)) *
                                        100
                                    }%`,
                                }}
                            />
                        ) : null}
                        <div {...props} />
                    </Fragment>
                )}
                renderThumb={(props, state) => (
                    <div {...props} tabIndex={disabled ? -1 : 0} />
                )}
                defaultValue={Number(value)}
                value={Number(value)}
                onChange={handleSliderChange}
                step={1}
            />
        </div>
    );
};

export default LeverageSlider;
